'use client';

import { pushCookieEvent } from '@/globals/analytics/events/pushCookieEvent';
import {
	createCookieStringStore,
	useCookieStore,
} from '@/globals/webStorage/useCookieStorage';
import {
	PropsWithChildren,
	createContext,
	useCallback,
	useContext,
	useMemo,
} from 'react';
import {
	CookieConsentState,
	CookieWidget,
	acceptCookiesName,
	cookieConsentOptions,
	cookieConsentStates,
} from './CookieConsent.config';

const cookieConsentContext = createContext<
	| [
			CookieConsentState | null,
			(state: CookieConsentState, widget: CookieWidget) => void,
	  ]
	| undefined
>(undefined);

const store = createCookieStringStore(
	acceptCookiesName,
	cookieConsentStates,
	cookieConsentOptions,
);

export const CookieConsentProviderClient = ({
	children,
	serverValue,
}: PropsWithChildren<{ serverValue: string | null }>) => {
	const [consentState, setConsentState] = useCookieStore(
		store,
		'unknown',
		serverValue,
	);

	const setCookieConsentState = useCallback(
		(state: CookieConsentState, widget: CookieWidget) => {
			setConsentState((oldState) => {
				pushCookieEvent({
					oldCookieConsent: oldState === null ? 'unknown' : oldState,
					cookieConsent: state,
					widget,
				});

				return state;
			});
		},
		[setConsentState],
	);

	return (
		<cookieConsentContext.Provider
			value={useMemo(
				() => [consentState, setCookieConsentState],
				[consentState, setCookieConsentState],
			)}
		>
			{children}
		</cookieConsentContext.Provider>
	);
};

export const useCookieConsent = () => {
	const cookieConsentContextValue = useContext(cookieConsentContext);

	if (cookieConsentContextValue == null)
		throw new Error('No cookie consent context found');

	return cookieConsentContextValue;
};
